import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useLocation } from 'react-router';
import { RootState } from '../../../store/store';
import { useSelector } from "react-redux";
import {
  Box,
  Grid,
  Typography,
  Tabs,
  Tab,
  Dialog,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  useMediaQuery,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AppTopBar from '../../app-bar/AppBar';
import FilterDropdown from '../../filter-dropdown/FilterDropDown';
import InsightsSection from './insights-section/InsightsSection';
import QlikVizPanel from '../../qlik-viz-panel/QlikVizPanel';
import Footer from '../../footer/Footer';
import { getQlikApp, getHyperCubeData } from '../../../services/qlikService';
import { applySelectMatch } from '../../../services/qlikSelectionService';
import { selectQlikAppSelections } from '../../../store/slice/qlikSelectionSlice';
import { useReady } from '../../../hooks/useReady';
import {
  INDUSTRY_FIELD,
  QLIK_APP_REF,
  COMPANY_ANNUAL_REV_FIELD,
  DATE_RANGE_FIELD,
  REGION_FIELD,
  FILTER_REMOVE_VALUES,
  INTERNATIONAL_VALUE
} from '../../../constants/qlik';
import { ExtraControlsConfig } from '../../../types';
import { PAGES } from '../../../constants/pages';
import { gtm } from '../../../services/gtm';

interface InsightLink {
  industry: string;
  type: string;
  title: string;
  link: string;
}

interface BusinessInsightLink {
  businessSize: string;
  type: string;
  title: string;
  link: string;
}

const IncidentGrowth: React.FC = () => {
  const hyperCubeCustomIdIndustry = "incident-growth|industry";
  const hyperCubeCustomIdBusiness = "incident-growth|business";
  const theme = useTheme();
  const location = useLocation();
  const currentPath = location.pathname;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const page = useMemo(() => PAGES['incident-growth'].contents ?? {}, []);
  const [searchParams] = useSearchParams();

  // State
  const [pageTitle] = useState(page.title.text || '');
  const [pageSubtitle] = useState(page.sub.text || '');
  const [claimsTabValue, setClaimsTabValue] = useState(0);
  const [raceTabValue, setRaceTabValue] = useState(0);
  const [industryLinks, setIndustryLinks] = useState<InsightLink[]>([]);
  const [businessLinks, setBusinessLinks] = useState<BusinessInsightLink[]>([]);
  const [selectedIndustry, setSelectedIndustry] = useState<string | null>(null);
  const [businessSize, setBusinessSize] = useState<string>('');
  const [showInsightsBox, setShowInsightsBox] = useState(true);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [videoSrc, setVideoSrc] = useState('');
  const [dateRangeRemoveValues, setDateRangeRemoveValues] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);

  const qlikAppSelections = useSelector((state: RootState) => selectQlikAppSelections(state, QLIK_APP_REF));
  
  const hyperCubeIndustry = useReady(QLIK_APP_REF, hyperCubeCustomIdIndustry).hyperCubeDataCustomId;
  const hyperCubeBusiness = useReady(QLIK_APP_REF, hyperCubeCustomIdBusiness).hyperCubeDataCustomId;
  const insightsIndustryHyperCubeData = useSelector(
    (state: RootState) => 
      state.qlikObject.qlikHyperCubesData[QLIK_APP_REF]?.[hyperCubeCustomIdIndustry]
  );
  const insightsBusinessHyperCubeData = useSelector(
    (state: RootState) => 
      state.qlikObject.qlikHyperCubesData[QLIK_APP_REF]?.[hyperCubeCustomIdBusiness]
  );

  useEffect(() => {
    initFromUrlParams();
  }, []);

  useEffect(() => {
    if (initialized) {
      subscribeToSelections();
    }
  }, [initialized, qlikAppSelections]);
  
  useEffect(() => {
    if (initialized) {
      if(!hyperCubeIndustry) {
        getIndustryInsights();
      }
      if(!hyperCubeBusiness) {
        getBusinessInsights();
      }
    }
  }, [initialized, hyperCubeIndustry, hyperCubeBusiness]);

  useEffect(() => {
    if (insightsIndustryHyperCubeData) {
      const matrix = insightsIndustryHyperCubeData.qDataPages[0].qMatrix;
      const links: InsightLink[] = matrix.map((row: any) => ({
        industry: row[0].qText,
        type: row[1].qText,
        title: row[2].qText,
        link: row[3].qText,
      }));

      setIndustryLinks(links);

      // Check if industry is selected
      if (matrix[0]?.[0]?.qState === 'S') {
        setSelectedIndustry(matrix[0][0].qText);
      } else {
        setSelectedIndustry(null);
      }
    }
  }, [insightsIndustryHyperCubeData]);

  useEffect(() => {
    if (insightsBusinessHyperCubeData) {
      const matrix = insightsBusinessHyperCubeData.qDataPages[0].qMatrix;
      const links: BusinessInsightLink[] = matrix.map((row: any) => ({
        businessSize: row[0].qText,
        type: row[1].qText,
        title: row[2].qText,
        link: row[3].qText,
      }));

      setBusinessLinks(links);
      setBusinessSize(matrix[0]?.[0]?.qText || '');
    }
  }, [insightsBusinessHyperCubeData]);

  const initFromUrlParams = async () => { //resume here
    try {
      // Get URL parameters
      const industry = searchParams.get('industry');
      const compRev = searchParams.get('compRev');
      const region = searchParams.get('region');
      const dateRange = searchParams.get('dateRange');

      // Apply selections from URL parameters
      if (industry) {
        await applySelectMatch(QLIK_APP_REF, INDUSTRY_FIELD, industry, "", currentPath);
      }
      if (compRev) {
        await applySelectMatch(QLIK_APP_REF, COMPANY_ANNUAL_REV_FIELD, compRev, "", currentPath);
      }
      if (region) {
        await applySelectMatch(QLIK_APP_REF, REGION_FIELD, region, "", currentPath);
      }
      if (dateRange) {
        await applySelectMatch(QLIK_APP_REF, DATE_RANGE_FIELD, dateRange, "", currentPath);
      }

      setInitialized(true);
    } catch (error) {
      console.error('Error initializing from URL params:', error);
      setInitialized(true);
    }
  };  

  const getIndustryInsights = async () => {
    try {
      const app = await getQlikApp(QLIK_APP_REF);
      if (!app) return;

      const industryInsightsHyperCubeDef = {
        qInitialDataFetch: [
            {
              qLeft: 0,
              qTop: 0,
              qWidth: 4,
              qHeight: 10
            }
        ],
        qDimensions : [
          {
            qDef : { qFieldDefs : ["Industry"]}
          }, 
          {
            qDef : { qFieldDefs : ["Type"] }
          },
          {
            qDef : { qFieldDefs : ["Title"] }
          },
          {
            qDef : { qFieldDefs : ["Link"] }
          }
        ],
        qMeasures : [{
          qDef : {
              qDef : "1"
          }
        }]
      }

      await getHyperCubeData(QLIK_APP_REF, hyperCubeCustomIdIndustry, industryInsightsHyperCubeDef);

    } catch (error) {
      console.error('Error getting industry insights:', error);
    }
  };

  const getBusinessInsights = async () => {
    try {
      const app = await getQlikApp(QLIK_APP_REF);
      if (!app) return;

      const businessInsightsHyperCubeDef = {
        qInitialDataFetch: [
            {
              qLeft: 0,
              qTop: 0,
              qWidth: 4,
              qHeight: 9
            }
        ],
        qDimensions : [{
          qDef : {
              qFieldDefs : ["Business Size"]
          }
        }, {
          qDef : {
              qFieldDefs : ["SizeContentType"]
          }
        }, {
          qDef : {
              qFieldDefs : ["SizeContentTitle"]
          }
        }, {
          qDef : {
              qFieldDefs : ["SizeContentLink"]
          }
        }],
        qMeasures : [{
          qDef : {
              qDef : "1"
          }
        }],
      }

      await getHyperCubeData(QLIK_APP_REF, hyperCubeCustomIdBusiness, businessInsightsHyperCubeDef);

    } catch (error) {
      console.error('Error getting business insights:', error);
    }
  };

  const subscribeToSelections = async () => {
    try {
      
      // Check if International region is selected
      const internationalSelected = qlikAppSelections?.some(
        (s: any) => s.qField === REGION_FIELD && s.qSelected === INTERNATIONAL_VALUE
      );

      if (internationalSelected) {
        setDateRangeRemoveValues(FILTER_REMOVE_VALUES);
      } else {
        setDateRangeRemoveValues([]);
      }
    } catch (error) {
      console.error('Error subscribing to selections:', error);
    }
  };

  const handleClaimsTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setClaimsTabValue(newValue);
    const tabName = newValue === 0 ? 'Industries' : 'Revenue Size';
    gtm.chartInteraction(`Tab Switch: ${tabName}`,'Global Claims Incident Growth');
  };

  const handleRaceTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setRaceTabValue(newValue);
    const tabName = newValue === 0 ? 'Industries' : 'Revenue Size';
    const chartTitle =
      newValue === 0
        ? 'Percentage of Annual Claims by Industry'
        : 'Percentage of Annual Claims by Revenue Size';
    gtm.chartInteraction(`Tab Switch: ${tabName}`, chartTitle);
  };

  const handleVideoClick = (src: string) => {
    setVideoSrc(src);
    setVideoModalOpen(true);
    gtm.chartInteraction('Insights Video', src);
  };

  const handleLinkClick = (src: string) => {
    gtm.chartInteraction('Insight Link', src);
  };

  // Filter elements
  const filterElements = (
    <>
      <FilterDropdown
        formName="Page Filters"
        fieldName={INDUSTRY_FIELD}
        fieldLabel="Industry"
        multiSelect={false}
        qlikSort="qSortByAscii"
      />
      <FilterDropdown
        formName="Page Filters"
        fieldName={COMPANY_ANNUAL_REV_FIELD}
        fieldLabel="Company Revenue"
        multiSelect={true}
        qlikSort="qSortByLoadOrder"
        sortExpression="SortValue"
      />
      <FilterDropdown
        formName="Page Filters"
        fieldName="Region"
        fieldLabel="Region"
        multiSelect={false}
        qlikSort="qSortByLoadOrder"
      />
      <FilterDropdown
        formName="Page Filters"
        fieldName="Date Range"
        fieldLabel="Date Range"
        multiSelect={false}
        qlikSort="qSortByLoadOrder"
        removeValues={dateRangeRemoveValues}
      />
    </>
  );

  // Extra controls config
  const extraControls: ExtraControlsConfig = {
    startOver: { enable: true },
    lastUpdate: { enable: true, componentLocation: 'nav' },
  };

  const showInsightsPanel = Boolean(
    false
    // 20260126: business request to do not show insights panel. Below logic kept for future reference.
    // (businessLinks.length !== 0 && businessLinks.length <= 3 || selectedIndustry) && !isMobile
  );

  return (
    <>
      <AppTopBar
        extraControls={extraControls}
        filterElements={filterElements}
      />

      {/* Insights Panel - Top Right */}
      <Box
        sx={{
          position: 'fixed',
          right: 0,
          top: 64, // Height of AppBar
          zIndex: 1101,
        }}
      >
        {/* Close Button - Outside Collapse, positioned on the left when open */}
        {showInsightsPanel && showInsightsBox && (
          <IconButton
            onClick={() => setShowInsightsBox(false)}
            sx={{
              position: 'absolute',
              left: 260, // Position to the left of the 300px panel (300 - 40)
              top: 0,
              width: 40,
              height: 40,
              backgroundColor: 'var(--mui-custom-navy)',
              color: 'white',
              borderRadius: '4px 0 0 4px',
              transition: 'left 0.3s',
              zIndex: 1501, // Above the panel
              '&:hover': {
                backgroundColor: 'var(--mui-custom-darknavy)',
              },
            }}
          >
            <ChevronRightIcon sx={{ color: 'white' }} />
          </IconButton>
        )}

        <Collapse 
          in={showInsightsPanel && showInsightsBox} 
          orientation="horizontal"
          timeout={300}
        >
          <Box
            sx={{
              backgroundColor: 'var(--mui-custom-navy)',
              color: 'white',
              boxShadow: 3,
              borderRadius: '8px 0 0 8px',
              width: 300,
              maxHeight: 'calc(100vh - 64px)',
              overflow: 'auto',
              position: 'relative',
              // Hide scrollbar but keep functionality
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none', // Firefox
              msOverflowStyle: 'none', // IE/Edge
            }}
          >
            {/* Industry Insights */}
            {selectedIndustry && (
              <InsightsSection
                title={`${selectedIndustry} Insights`}
                links={industryLinks}
                onVideoClick={handleVideoClick}
                onLinkClick={handleLinkClick}
              />
            )}

            {/* Business Size Insights */}
            {businessLinks.length !== 0 && businessLinks.length <= 3 && (
              <InsightsSection
                title={`${businessSize} Size Business Insights`}
                links={businessLinks}
                onVideoClick={handleVideoClick}
                onLinkClick={handleLinkClick}
              />
            )}
          </Box>
        </Collapse>

        {/* Open Button (when closed) - Top Right Corner */}
        {showInsightsPanel && !showInsightsBox && (
          <IconButton
            onClick={() => setShowInsightsBox(true)}
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              width: 40,
              height: 40,
              backgroundColor: 'var(--mui-custom-navy)',
              color: 'white',
              borderRadius: '4px 0 0 4px',
              transition: 'all 0.3s',
              opacity: 0.5,
              '&:hover': {
                opacity: 1,
                backgroundColor: 'var(--mui-custom-navy)',
              },
            }}
          >
            <ChevronRightIcon sx={{ transform: 'rotate(180deg)', color: 'white' }} />
          </IconButton>
        )}
      </Box>

      {/* Main Content - Using Grid */}
      <Grid
        container
        component="main"
        sx={{
          py: 2,
          px: { xs: 2, sm: 2, md: 2 },
          // Add margin for the left drawer on desktop (md and up)
          // The drawer is 57px when collapsed, 257px when expanded
          ml: { xs: 0, md: '57px' }, // Collapsed drawer width
          transition: 'margin-left 0.3s',
          // Adjust for insights panel if shown
          ...(showInsightsPanel && showInsightsBox && {
            ml: { xs: 0, md: 'calc(57px)' } // Drawer + insights panel
          })
        }}
      >
        {/* Page Title and Subtitle */}
        <Grid size={{ xs: 12 }}>
          <Box sx={{ display: { xs: 'block', md: 'none' }, textAlign: 'center', mb: 2 }}>
              <Typography
                  variant="h5"
                  sx={{ mb: 1, fontWeight: 400 }}
              >
                  {pageTitle}
              </Typography>
              <Box sx={{ borderTop: 3, borderColor: 'primary.main', width: 60, mx: 'auto', mb: 2 }} />
          </Box>

          <Box sx={{ display: { xs: 'none', md: 'block' }, mb: 3 }}>
              <Typography
                  variant="h5"
                  sx={{ mb: 2, fontWeight: 400 }}
              >
                  {pageTitle}
              </Typography>
              <Box sx={{ borderLeft: 3, borderColor: 'primary.main', pl: 2 }}>
                  <Typography
                      variant="body1"
                  >
                      {pageSubtitle}
                  </Typography>
              </Box>
          </Box>
      </Grid>

        {/* Claims Growth Tabs */}
        <Grid size={{ xs: 12 }} sx={{ mb: 4 }}>
          <Tabs
            value={claimsTabValue}
            onChange={handleClaimsTabChange}
            sx={{ 
              '& .MuiTabs-indicator': {
              height: 0, // Make the active tab indicator thicker if desired
              }
          }}
          >
            <Tab 
              label="Industries" 
              sx={{
                textTransform: 'none',
                transition: 'all 0.3s',
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                }
              }}
            />
            <Tab label="Revenue Size"
              sx={{
                textTransform: 'none',
                transition: 'all 0.3s',
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                }
              }}
            />
          </Tabs>

          {claimsTabValue === 0 && (
            <QlikVizPanel
              qlikVizName="inc-growth-global-inc-ind"
              manualLegend={[
                { name: 'Overall', color: '#000' },
                { name: 'Professional Services', color: '#150F96' },
                { name: 'Technology', color: '#01C1D6' },
                { name: 'Manufacturing', color: '#F1352B' },
                { name: 'Healthcare', color: '#ffb617' },
                { name: 'Financial Institutions', color: '#4B4E53' },
                { name: 'Public Entity', color: '#AFAFAF' },
                { name: 'Education', color: '#7ACB00' },
                { name: 'Retail/Hospitality', color: '#FF6600' },
                { name: 'Entertainment/Media', color: '#6E27C5' },
                { name: 'Other', color: '#FF0198' },
              ]}
              alwaysShow="Overall"
              showCriteria={true}
              manualLegendTitle="Select an Industry for Comparison"
              fieldName="Industry"
              qlikSort="qSortByAscii"
              fieldLabel="Industry"
            />
          )}

          {claimsTabValue === 1 && (
            <QlikVizPanel
              qlikVizName="inc-growth-global-inc-rev"
              manualLegend={[
                { name: 'Overall', color: '#000' },
                { name: 'Under $25M', color: '#6E27C5' },
                { name: '$25.1M to $150M', color: '#FF6600' },
                { name: '$151M to $500M', color: '#150F96' },
                { name: 'Over $501M', color: '#4B4E53' },
              ]}
              alwaysShow="Overall"
              showCriteria={true}
              manualLegendTitle="Select Company Revenue Size for Comparison"
              fieldName="CompanyAnnualRevenue_Forecast"
              qlikSort="qSortByExpression"
              fieldLabel="Company Revenue"
              sortExpression="SortValue_Forecast"
              multiSelect={true}
            />
          )}
        </Grid>

        {/* Bar Chart Race Tabs */}
        <Grid size={{ xs: 12 }} sx={{ mb: 4 }}>
          {/* <Tabs
            value={raceTabValue}
            onChange={handleRaceTabChange}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            <Tab label="Industries" />
            <Tab label="Revenue Size" />
          </Tabs> */}
          
          <Tabs
            value={raceTabValue}
            onChange={handleRaceTabChange}
            sx={{ 
              '& .MuiTabs-indicator': {
              height: 0, // Make the active tab indicator thicker if desired
              }
          }}
          >
            <Tab 
              label="Industries" 
              sx={{
                textTransform: 'none',
                transition: 'all 0.3s',
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                }
              }}
            />
            <Tab label="Revenue Size"
              sx={{
                textTransform: 'none',
                transition: 'all 0.3s',
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                }
              }}
            />
          </Tabs>

          {raceTabValue === 0 && <QlikVizPanel qlikVizName="inc-growth-perc-ann-claim-ind" svgToPng={true} />}

          {raceTabValue === 1 && <QlikVizPanel qlikVizName="inc-growth-perc-ann-claim-rev" svgToPng={true} />}
        </Grid>

        {/* Activity Pies Panel */}
        <Grid size={{ xs: 12 }}>
          <Box
            sx={{
              backgroundColor: 'white',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
            }}
          >
            {/* Desktop Filters */}
            {!isMobile && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <FilterDropdown
                    formName="Page Filters"
                    fieldName="Industry"
                    fieldLabel="Industry"
                    multiSelect={false}
                    qlikSort="qSortByAscii"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <FilterDropdown
                    formName="Page Filters"
                    fieldName="Company Annual Revenue"
                    fieldLabel="Company Revenue"
                    multiSelect={true}
                    qlikSort="qSortByLoadOrder"
                    sortExpression="SortValue"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <FilterDropdown
                    formName="Page Filters"
                    fieldName="Region"
                    fieldLabel="Region"
                    multiSelect={false}
                    qlikSort="qSortByLoadOrder"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <FilterDropdown
                    formName="Page Filters"
                    fieldName="Date Range"
                    fieldLabel="Date Range"
                    multiSelect={false}
                    qlikSort="qSortByLoadOrder"
                    removeValues={dateRangeRemoveValues}
                  />
                </Grid>
              </Grid>
            )}

            {/* Activity Pies */}
            <Grid container spacing={3} sx={{ alignItems: { xs: 'flex-start', md: 'stretch' } }}>
              <Grid size={{ xs: 12, md: 4 }} sx={{ display: { xs: 'block', md: 'flex' } }}>
                <QlikVizPanel
                  qlikVizName="inc-growth-actions-pie-1"
                  legendName="inc-growth-actions-legend-pie-1"
                  flexColumn={true}
                  expand={true}
                  equalHeight={!isMobile}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }} sx={{ display: { xs: 'block', md: 'flex' } }}>
                <QlikVizPanel
                  qlikVizName="inc-growth-assess-actors-pie-2"
                  legendName="inc-growth-assess-actors-legend-pie-2"
                  flexColumn={true}
                  expand={true}
                  equalHeight={!isMobile}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }} sx={{ display: { xs: 'block', md: 'flex' } }}>
                <QlikVizPanel
                  qlikVizName="inc-growth-assets-pie-3"
                  legendName="inc-growth-assets-legend-pie-3"
                  flexColumn={true}
                  expand={true}
                  enableInteractions={false}
                  equalHeight={!isMobile}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>

      {/* Footer */}
      <Footer componentLocation="dashboard" />

      {/* Video Modal */}
      <Dialog
        open={videoModalOpen}
        onClose={() => setVideoModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ position: 'relative', p: 0 }}>
          <IconButton
            onClick={() => setVideoModalOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              zIndex: 1,
              backgroundColor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.7)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="iframe"
            src={videoSrc}
            sx={{
              width: '100%',
              height: '60vh',
              border: 'none',
            }}
            title="Video"
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default IncidentGrowth;